/**
 * What to tell someone when the workspace lookup fails.
 *
 * This used to be a single branch: anything that was not a 404 said "this is
 * usually temporary — try again in a moment". That sent the reader to the
 * wrong place twice over. It blamed the organization's settings when the
 * cause was the API being down, and it advised waiting in the one case where
 * waiting never helps — a core-service that will not boot stays down until
 * somebody fixes it, not until the reader retries.
 *
 * The three cases need three different answers, so they get three:
 *   no response   the request never reached a server at all
 *   5xx           a server answered and failed, which nobody can retry away
 *   anything else a server answered and refused
 *
 * Each carries a `technical` line. Showing "HTTP 500" on the screen is what
 * turns "is this tenant resolution?" into "the API is down" without reading
 * a log.
 */
export function describeBootFailure(status: number | undefined): {
  heading: string;
  body: string;
  tone: 'amber' | 'rose';
  technical: string;
} {
  if (status === undefined) {
    return {
      heading: 'Can’t reach your workspace',
      tone: 'amber',
      body: 'Your browser could not reach the server. Check your connection and try again. If other sites load normally, the service itself may be offline.',
      technical: 'No response from server',
    };
  }

  if (status >= 500) {
    return {
      heading: 'Workspace is temporarily unavailable',
      tone: 'rose',
      body: 'The server is reachable but could not return your organization’s settings. This is a fault on our side rather than something retrying will clear. Please tell your administrator if it continues.',
      technical: `HTTP ${status}`,
    };
  }

  return {
    heading: 'Can’t load your workspace',
    tone: 'amber',
    body: 'The server refused the request for your organization’s settings. Please contact your administrator if this continues.',
    technical: `HTTP ${status}`,
  };
}
